import { useSearchParams } from "react-router-dom";
// Worker Competency Register — skills matrix (0-4 scale) + per-worker uploaded certificates.
import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Download, FileText, Plus, Sparkles, Trash2, X, Pencil, Wrench, Upload, ExternalLink, ScrollText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// 0-4 skill-scale colour & label helpers (NZ civil convention).
const SCALE = {
  0: { label: "Not trained", short: "0", cls: "bg-zinc-200 text-zinc-700 border-zinc-400" },
  1: { label: "Full supervision", short: "1", cls: "bg-red-100 text-red-800 border-red-400" },
  2: { label: "Partial supervision", short: "2", cls: "bg-amber-200 text-amber-900 border-amber-500" },
  3: { label: "Competent unsupervised", short: "3", cls: "bg-emerald-100 text-emerald-800 border-emerald-500" },
  4: { label: "Can train others", short: "4", cls: "bg-emerald-600 text-white border-emerald-700" },
};

const CAT_LABEL = { hand_tools: "Hand tools", skills: "Site skills", other: "Other" };

const certStatus = (exp, today) => {
  if (!exp) return { cls: "bg-zinc-100 text-zinc-600 border-zinc-300", label: "On file" };
  if (exp < today) return { cls: "bg-red-100 text-red-800 border-red-400", label: "EXPIRED" };
  const d = (new Date(exp) - new Date(today)) / 86400000;
  if (d <= 7) return { cls: "bg-orange-500 text-white border-orange-600", label: `≤7d` };
  if (d <= 30) return { cls: "bg-amber-300 text-zinc-900 border-amber-400", label: `≤30d` };
  return { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: exp };
};

export default function Competencies() {
  const [matrix, setMatrix] = useState({ workers: [], types: [], cells: {}, today: "" });
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null); // {worker, type, existing?}
  const [managingTypes, setManagingTypes] = useState(false);
  const [certsWorker, setCertsWorker] = useState(null);
  const [params] = useSearchParams();
  useEffect(() => {
    const id = params.get("worker");
    if (!id || loading) return;
    const worker = matrix.workers.find(w => w.id === id);
    if (worker) setCertsWorker(worker);
    else toast.error("The linked person is unavailable in this register.");
  }, [params, loading, matrix.workers]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/competencies/matrix");
      setMatrix(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    if (!window.confirm("Add the NZ civil hand tools & site skills set (~17 items)? Existing custom items keep their place.")) return;
    try {
      const { data } = await api.post("/competency-types/seed-nz-standard");
      toast.success(`Added ${data.added} skills/tools`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    }
  };

  const legacyCount = useMemo(
    () => (matrix.types || []).filter((t) => t.category !== "hand_tools" && t.category !== "skills").length,
    [matrix.types]
  );

  const cleanupLegacy = async () => {
    if (!window.confirm(`Archive ${legacyCount} legacy certificate item${legacyCount === 1 ? "" : "s"} from the matrix? They'll be hidden from the register. Upload the actual certificate file under each worker's Certificates section instead.`)) return;
    try {
      const { data } = await api.post("/competency-types/cleanup-non-skills");
      toast.success(`Archived ${data.archived} legacy items`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    }
  };

  const csvUrl = `${process.env.REACT_APP_BACKEND_URL}/api/competencies/matrix.csv`;
  const passportUrl = (uid) => `${process.env.REACT_APP_BACKEND_URL}/api/workers/${uid}/competencies/passport.pdf`;

  const hasTypes = matrix.types.length > 0;

  return (
    <div data-testid="competencies-page">
      <PageHeader
        tag="/ People"
        title="Competency register"
        subtitle="Rate every crew member 0–4 on the hand tools and site skills they use. Upload tickets and certs (Site Safe, Class 4, First Aid, etc.) as files on each worker."
        action={
          <div className="flex gap-2 flex-wrap">
            {hasTypes && (
              <a href={csvUrl} className="inline-flex items-center px-3 h-9 border-2 border-zinc-300 hover:border-zinc-500 text-xs font-bold uppercase tracking-wider rounded-sm" data-testid="comp-csv">
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV export
              </a>
            )}
            <Button onClick={() => setManagingTypes(true)} variant="outline" className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="comp-manage-types">
              <Wrench className="w-3.5 h-3.5 mr-1.5" /> Manage tools &amp; skills
            </Button>
            <Button onClick={seed} variant="outline" className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="comp-seed">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Seed NZ standard set
            </Button>
          </div>
        }
      />

      {legacyCount > 0 && (
        <div className="mb-3 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="comp-legacy-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold uppercase tracking-wider text-xs text-amber-900">{legacyCount} legacy certificate item{legacyCount === 1 ? "" : "s"} still in your matrix</div>
            <div className="text-xs text-zinc-700 mt-1">Certificates (Site Safe, Class 4, First Aid, etc.) are now uploaded as files on each worker — not tracked as columns. Clean these up to tidy the matrix.</div>
          </div>
          <Button size="sm" onClick={cleanupLegacy} className="rounded-sm bg-amber-700 hover:bg-amber-800 text-white uppercase tracking-wider font-bold text-xs" data-testid="comp-cleanup-legacy">
            Archive legacy items
          </Button>
        </div>
      )}

      {!loading && hasTypes && (
        <div className="mb-3 border border-zinc-300 bg-zinc-50 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-700 flex flex-wrap items-center gap-2" data-testid="comp-scale-legend">
          <span className="text-zinc-500">Skill scale —</span>
          {[0, 1, 2, 3, 4].map((n) => (
            <span key={n} className={`inline-flex items-center gap-1 border px-1.5 py-0.5 ${SCALE[n].cls}`}>
              <span className="font-mono font-black">{n}</span>
              <span className="font-normal normal-case tracking-normal text-[10px]">{SCALE[n].label}</span>
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => (<div key={i} className="h-12 bg-zinc-100 animate-pulse border border-zinc-200" />))}</div>
      ) : !hasTypes ? (
        <div className="border-2 border-dashed border-zinc-300 p-12 text-center" data-testid="comp-empty">
          <ShieldCheck className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <div className="font-display font-bold uppercase tracking-tight text-lg">Your competency register is empty</div>
          <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">Seed the NZ civil hand-tools &amp; site-skills set, or use “Manage tools &amp; skills” to add your own. Upload certs &amp; tickets directly to each worker.</p>
          <Button onClick={seed} className="mt-4 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Seed the NZ standard set
          </Button>
        </div>
      ) : (
        <div className="border border-zinc-300 bg-white overflow-x-auto" data-testid="comp-matrix">
          <table className="text-xs min-w-full">
            <thead className="bg-zinc-100 border-b-2 border-zinc-300">
              <tr>
                <th className="px-3 py-2 text-left sticky left-0 bg-zinc-100 z-10 border-r border-zinc-200 min-w-[220px]">Worker</th>
                {matrix.types.map((t) => (
                  <th key={t.id} className="px-2 py-2 text-left font-bold text-[9px] uppercase tracking-widest whitespace-nowrap" title={t.issuer || ""}>
                    <button
                      type="button"
                      onClick={() => setManagingTypes({ focus: t.id })}
                      className="hover:text-[#FF5F15] text-left"
                      data-testid={`comp-th-${t.id}`}
                      title="Edit"
                    >
                      <div className="truncate max-w-[120px]" title={t.name}>{t.name}</div>
                      <div className="text-zinc-400 text-[8px] font-normal">{CAT_LABEL[t.category] || t.category}</div>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {matrix.workers.map((u) => (
                <tr key={u.id} className="hover:bg-zinc-50" data-testid={`comp-row-${u.id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white border-r border-zinc-200 z-10">
                    <div className="font-bold">{u.name || u.email}</div>
                    <div className="text-[10px] text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{u.role}</span>
                      <button
                        type="button"
                        onClick={() => setCertsWorker(u)}
                        className="text-[#FF5F15] inline-flex items-center hover:underline"
                        data-testid={`comp-certs-${u.id}`}
                      >
                        <ScrollText className="w-3 h-3 mr-0.5" /> Certificates
                      </button>
                      <a href={passportUrl(u.id)} target="_blank" rel="noreferrer" className="text-[#FF5F15] inline-flex items-center hover:underline" data-testid={`comp-passport-${u.id}`}>
                        <FileText className="w-3 h-3 mr-0.5" /> Passport PDF
                      </a>
                    </div>
                  </td>
                  {matrix.types.map((t) => {
                    const cell = matrix.cells?.[u.id]?.[t.id];
                    const sl = cell && cell.skill_level != null ? SCALE[cell.skill_level] : null;
                    return (
                      <td key={t.id} className="px-2 py-2 align-top">
                        {cell ? (
                          <button
                            onClick={() => setEditingCell({ worker: u, type: t, existing: cell })}
                            className={`block w-full text-left text-[10px] font-bold border px-1.5 py-1 ${sl ? sl.cls : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}
                            data-testid={`comp-cell-${u.id}-${t.id}`}
                            title={sl ? `${sl.short} — ${sl.label}` : ""}
                            aria-label={`${u.name || u.email || "Worker"}: ${t.name}, ${sl ? `${sl.short} — ${sl.label}` : "unrated"}`}
                          >
                            {sl ? (
                              <span className="font-mono text-[14px] font-black leading-none">{sl.short}</span>
                            ) : "—"}
                          </button>
                        ) : (
                          <button
                            onClick={() => setEditingCell({ worker: u, type: t })}
                            className="text-[18px] text-zinc-300 hover:text-[#FF5F15] w-full text-center"
                            data-testid={`comp-add-${u.id}-${t.id}`}
                            title="Add"
                          >+</button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingCell && (
        <CompetencyDialog
          worker={editingCell.worker}
          type={editingCell.type}
          existing={editingCell.existing}
          onClose={() => setEditingCell(null)}
          onSaved={() => { setEditingCell(null); load(); }}
        />
      )}

      {managingTypes && (
        <ManageTypesDialog
          types={matrix.types}
          focusId={typeof managingTypes === "object" ? managingTypes.focus : null}
          onClose={() => setManagingTypes(false)}
          onChanged={load}
        />
      )}

      {certsWorker && (
        <WorkerCertsDialog
          worker={certsWorker}
          onClose={() => setCertsWorker(null)}
        />
      )}
    </div>
  );
}

function CompetencyDialog({ worker, type, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    notes: existing?.notes || "",
    skill_level: existing?.skill_level != null ? String(existing.skill_level) : "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const slNum = form.skill_level === "" ? null : Number(form.skill_level);
      const body = {
        competency_type_id: type.id,
        notes: form.notes || null,
        skill_level: slNum,
      };
      if (existing?.id) {
        await api.patch(`/competencies/${existing.id}`, body);
      } else {
        await api.post(`/workers/${worker.id}/competencies`, body);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!existing?.id) return;
    if (!window.confirm("Remove this skill rating?")) return;
    try {
      await api.delete(`/competencies/${existing.id}`);
      toast.success("Removed");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="comp-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">{type.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Rate <strong>{worker.name || worker.email}</strong> on this {CAT_LABEL[type.category] || "skill"}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="label-tag">Skill level (0–4)</Label>
            <div className="mt-1.5 grid grid-cols-5 gap-1.5" data-testid="comp-dlg-scale">
              {[0, 1, 2, 3, 4].map((n) => {
                const active = form.skill_level === String(n);
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, skill_level: active ? "" : String(n) })}
                    className={`border-2 px-1 py-2 text-center transition ${active ? SCALE[n].cls + " ring-2 ring-offset-1 ring-[#FF5F15]" : "border-zinc-300 bg-white hover:border-zinc-500 text-zinc-700"}`}
                    data-testid={`comp-dlg-scale-${n}`}
                    title={SCALE[n].label}
                  >
                    <div className="font-mono font-black text-base leading-none">{n}</div>
                    <div className="text-[9px] uppercase tracking-wider mt-1 leading-tight">{SCALE[n].label}</div>
                  </button>
                );
              })}
            </div>
            {form.skill_level !== "" && (
              <button
                type="button"
                onClick={() => setForm({ ...form, skill_level: "" })}
                className="mt-1.5 text-[10px] uppercase tracking-wider text-zinc-500 hover:text-red-600"
                data-testid="comp-dlg-scale-clear"
              >
                Clear skill level
              </button>
            )}
          </div>
          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400 text-sm" data-testid="comp-dlg-notes" placeholder="Optional — e.g. last used Aug 2025, refresher booked, etc." />
          </div>
        </div>
        <DialogFooter>
          {existing?.id && (
            <Button variant="outline" onClick={del} className="rounded-sm uppercase tracking-wider font-bold text-xs text-red-700 mr-auto" data-testid="comp-dlg-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="comp-dlg-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Manage tools & skills (catalogue CRUD) ----------------------------------
function ManageTypesDialog({ types, focusId, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // type or { __new: true }
  const visible = (types || []).filter((t) => !t.archived);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="manage-types-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">Tools &amp; skills catalogue</DialogTitle>
          <DialogDescription className="text-xs">
            Add the tools and skills your crew actually use. Each appears as a column on the matrix so you can rate every worker 0–4.
          </DialogDescription>
        </DialogHeader>

        <div className="border border-zinc-300 rounded-sm max-h-[55vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px]">Name</th>
                <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px]">Category</th>
                <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-[10px]">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {visible.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-zinc-500 text-xs">No tools or skills yet — add your first below.</td></tr>
              )}
              {visible.map((t) => (
                <tr key={t.id} className={focusId === t.id ? "bg-amber-50" : ""} data-testid={`type-row-${t.id}`}>
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2 text-zinc-500">{CAT_LABEL[t.category] || t.category}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setEditing(t)}
                      className="text-[#FF5F15] hover:underline inline-flex items-center text-[11px] font-bold uppercase tracking-wider"
                      data-testid={`type-edit-${t.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Close</Button>
          <Button onClick={() => setEditing({ __new: true })} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="type-add-new">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add new tool / skill
          </Button>
        </DialogFooter>

        {editing && (
          <TypeEditorDialog
            type={editing.__new ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); onChanged(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TypeEditorDialog({ type, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: type?.name || "",
    category: type?.category || "hand_tools",
    notes: type?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const body = { name: form.name.trim(), category: form.category, notes: form.notes || null };
      if (type?.id) {
        await api.patch(`/competency-types/${type.id}`, body);
      } else {
        await api.post(`/competency-types`, body);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!type?.id) return;
    if (!window.confirm(`Delete "${type.name}"? Existing worker ratings for this item will be removed.`)) return;
    try {
      await api.delete(`/competency-types/${type.id}`);
      toast.success("Deleted");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="type-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">{type ? "Edit" : "New"} tool / skill</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="label-tag">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400" placeholder="e.g. Plate compactor, Vibration roller" data-testid="type-name" autoFocus />
          </div>
          <div>
            <Label className="label-tag">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="type-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hand_tools">Hand tools</SelectItem>
                <SelectItem value="skills">Site skills</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Internal notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400 text-sm" data-testid="type-notes" />
          </div>
        </div>
        <DialogFooter>
          {type?.id && (
            <Button variant="outline" onClick={del} className="rounded-sm uppercase tracking-wider font-bold text-xs text-red-700 mr-auto" data-testid="type-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="type-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Worker certificates (uploaded PDF/image cert files) ---------------------
function WorkerCertsDialog({ worker, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [today] = useState(new Date().toISOString().slice(0, 10));
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workers/${worker.id}/certificates`);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load certificates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [worker.id]);

  const remove = async (cid) => {
    if (!window.confirm("Remove this certificate?")) return;
    try {
      await api.delete(`/certificates/${cid}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const viewUrl = (path) => {
    const tok = localStorage.getItem("fl_token");
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/view?path=${encodeURIComponent(path)}&auth=${tok || ""}`;
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="certs-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">Certificates · {worker.name || worker.email}</DialogTitle>
          <DialogDescription className="text-xs">
            Upload the actual cert file (PDF or image) — Site Safe Passport, Class 4 licence, First Aid, STMS, plant tickets, etc.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-xs text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 p-8 text-center" data-testid="certs-empty">
            <ScrollText className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <div className="text-sm font-bold uppercase tracking-tight">No certificates uploaded yet</div>
            <div className="text-xs text-zinc-500 mt-1">Upload PDFs or photos of this worker&apos;s tickets and certs.</div>
          </div>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {items.map((c) => {
              const s = certStatus(c.expiry_date, today);
              return (
                <div key={c.id} className="border border-zinc-300 px-3 py-2.5 flex items-start gap-3" data-testid={`cert-row-${c.id}`}>
                  <ScrollText className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      {c.issuer && <span>Issuer: <strong>{c.issuer}</strong></span>}
                      {c.cert_number && <span>Cert #: <span className="font-mono">{c.cert_number}</span></span>}
                      {c.issued_date && <span>Issued {c.issued_date}</span>}
                      {c.expiry_date && <span>Expires {c.expiry_date}</span>}
                    </div>
                    {c.notes && <div className="text-[11px] text-zinc-600 mt-1 italic">{c.notes}</div>}
                  </div>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${s.cls}`}>{s.label}</span>
                  <a href={viewUrl(c.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center px-2 h-7 border border-zinc-300 hover:border-zinc-500 text-[10px] font-bold uppercase tracking-wider rounded-sm" data-testid={`cert-view-${c.id}`}>
                    <ExternalLink className="w-3 h-3 mr-1" /> View
                  </a>
                  <button onClick={() => remove(c.id)} className="text-red-700 hover:text-red-900 p-1" data-testid={`cert-del-${c.id}`} title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Close</Button>
          <Button onClick={() => setAdding(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="cert-add-new">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload certificate
          </Button>
        </DialogFooter>

        {adding && (
          <CertUploadDialog
            workerId={worker.id}
            onClose={() => setAdding(false)}
            onSaved={() => { setAdding(false); load(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CertUploadDialog({ workerId, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    issuer: "",
    cert_number: "",
    issued_date: "",
    expiry_date: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Certificate name is required");
      return;
    }
    if (!file) {
      toast.error("Please attach the certificate file (PDF or image)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "competency_cert");
      const { data: f } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/workers/${workerId}/certificates`, {
        name: form.name.trim(),
        file_path: f.storage_path,
        file_name: f.original_filename,
        content_type: f.content_type,
        issuer: form.issuer || null,
        cert_number: form.cert_number || null,
        issued_date: form.issued_date || null,
        expiry_date: form.expiry_date || null,
        notes: form.notes || null,
      });
      toast.success("Certificate uploaded");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="cert-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-tight">Upload certificate</DialogTitle>
          <DialogDescription className="text-xs">Add a PDF or image of the ticket/cert plus its key details.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="label-tag">Certificate name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Site Safe Passport · Class 4 Licence" className="mt-1.5 rounded-sm border-zinc-400" data-testid="cert-name" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Issuer</Label>
              <Input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="e.g. Site Safe NZ, NZTA" className="mt-1.5 rounded-sm border-zinc-400" data-testid="cert-issuer" />
            </div>
            <div>
              <Label className="label-tag">Cert / Licence #</Label>
              <Input value={form.cert_number} onChange={(e) => setForm({ ...form, cert_number: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400 font-mono" data-testid="cert-number" />
            </div>
            <div>
              <Label className="label-tag">Issued</Label>
              <Input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400" data-testid="cert-issued" />
            </div>
            <div>
              <Label className="label-tag">Expires</Label>
              <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400" data-testid="cert-expiry" />
            </div>
          </div>
          <div>
            <Label className="label-tag">Notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm border-zinc-400 text-sm" data-testid="cert-notes" />
          </div>
          <div>
            <Label className="label-tag">Certificate file (PDF or image) *</Label>
            <div className="mt-1.5">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-2 file:border-zinc-300 file:bg-white file:text-xs file:font-bold file:uppercase file:tracking-wider hover:file:border-[#FF5F15] cursor-pointer"
                data-testid="cert-file"
              />
              {file && <div className="text-[11px] text-zinc-600 mt-1">{file.name} · {Math.round(file.size / 1024)} KB</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold text-xs">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="cert-upload-save">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
